import { useState } from 'react'
import { inject, observer } from 'mobx-react'

import { DataTable, DeleteModal, Drawer, HelpIcon, TooltipText } from '../../../components'
import { TABLET_VIEWPORT } from '../../../guided-toolbox/utils/breakpoints'
import { numberToDollars, withRouterProps } from '../../../utils'
import { GoalForm } from '../forms'
import { formatGoalWhen } from './goalDisplay'
import {
  Section,
  OtherGoalsHeader,
  IntroCopy,
  IntroLead,
  IntroHelp,
  AddGoalButton,
  GoalTable,
  GoalNameButton,
  GoalMenu,
  EmptyGoals,
} from './GoalsDashboardStyle'

const BIG_TICKET_PROMPT = 'Planning to buy a “big ticket” item with retirement savings?'
const NO_GOALS_MESSAGE = "You haven't added any other goals yet."

const COLUMNS = [
  { key: 'name', label: 'Name', fr: 260 },
  { key: 'when', label: 'When', fr: 140 },
  { key: 'amount', label: 'Amount needed', align: 'right', fr: 140 },
]

const TABLE_LAYOUT = {
  columnGap: '0',
  trailingWidth: '150fr',
}

const OtherGoalsSection = ({ match, navigate, store }) => {
  const { currentGoals, pastGoals } = store
  const [goalPendingDelete, setGoalPendingDelete] = useState(null)

  // Keep this separate from `goalPendingDelete` so the modal remains open
  // while the delete is in flight.
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteFailed, setDeleteFailed] = useState(false)

  const goals = [...currentGoals, ...pastGoals]

  // Past goals are already funded, so only upcoming ones count toward the total.
  const plannedTotal = currentGoals.reduce((total, goal) => total + goal.totalRemaining, 0)

  const editingId = Number(match.params.id)
  const goalBeingEdited = store.viewGoalById(editingId)
  const openGoal = goal => navigate(`/goals/${goal.id}`)
  const closeGoal = () => navigate('/goals')

  const openDeleteModal = goal => {
    setDeleteFailed(false)
    setGoalPendingDelete(goal)
  }

  const handleDelete = async () => {
    setIsDeleting(true)

    try {
      const deletedId = goalPendingDelete.id
      await store.deleteGoal(goalPendingDelete)
      setGoalPendingDelete(null)

      // Close the drawer if its route points to the deleted goal.
      if (deletedId === editingId) {
        closeGoal()
      }
    } catch (err) {
      console.error(err)
      setDeleteFailed(true)
    } finally {
      setIsDeleting(false)
    }
  }

  const rows = goals.map(goal => ({
    id: goal.id,
    domId: `goal-${goal.id}`,
    muted: goal.isPast,

    // No `cardTitle`: the name cell doubles as the mobile card title, so the name stays tappable.
    values: {
      name: (
        <GoalNameButton type='button' onClick={() => openGoal(goal)}>
          {goal.name}
        </GoalNameButton>
      ),
      when: formatGoalWhen(goal),
      amount: `$${numberToDollars(goal.totalRemaining, true)}`,
    },
    trailing: (
      <GoalMenu
        ariaLabel={`Actions for ${goal.name}`}
        items={[
          { label: goal.isPast ? 'View' : 'Edit', onClick: () => openGoal(goal) },
          { label: 'Remove', onClick: () => openDeleteModal(goal), danger: true },
        ]}
      />
    ),
  }))

  return (
    <Section>
      <OtherGoalsHeader>
        <IntroCopy>
          {plannedTotal > 0 ? (
            <>
              You&rsquo;re planning on using{' '}
              <IntroLead>${numberToDollars(plannedTotal, true)}</IntroLead> of retirement savings
              for other <IntroLead>goals</IntroLead>
            </>
          ) : (
            <>
              {BIG_TICKET_PROMPT}
              <IntroHelp>
                <HelpIcon tooltip={TooltipText.whatIsAGoal()} />
              </IntroHelp>
            </>
          )}
        </IntroCopy>

        <AddGoalButton type='button' onClick={() => navigate('/goals/add')}>
          + Add a goal
        </AddGoalButton>
      </OtherGoalsHeader>

      <GoalTable>
        {/* Replace the table so the empty state has no column headers. */}
        {goals.length === 0 ? (
          <EmptyGoals>{NO_GOALS_MESSAGE}</EmptyGoals>
        ) : (
          <DataTable columns={COLUMNS} rows={rows} breakpoint={TABLET_VIEWPORT} {...TABLE_LAYOUT} />
        )}
      </GoalTable>

      <Drawer
        active={Boolean(goalBeingEdited)}
        subtitle={goalBeingEdited && (goalBeingEdited.isPast ? 'View goal' : 'Edit goal')}
        title={goalBeingEdited?.name}
        width='60%'
        responsiveWidth
        onOverlayClick={closeGoal}
        onEscKeyDown={closeGoal}>
        {goalBeingEdited && (
          <GoalForm
            handleClose={closeGoal}
            isEdit
            foundGoal={goalBeingEdited}
            readOnly={goalBeingEdited.isPast}
          />
        )}
      </Drawer>

      <DeleteModal
        handleConfirmation={handleDelete}
        isModalOpen={Boolean(goalPendingDelete)}
        title='Delete goal'
        toggleModal={() => setGoalPendingDelete(null)}
        loading={isDeleting}
        error={deleteFailed}
      />
    </Section>
  )
}

export default withRouterProps(inject('store')(observer(OtherGoalsSection)))
